import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Pause, Play } from "lucide-react";
import { ModuleIntroModal } from "../../module/ModuleIntroModal";
import { ModuleStudioShell } from "../../module/ModuleStudioShell";
import { GymCountdownOverlay, type GymCountdownPhase } from "../../gym/GymCountdownOverlay";
import { PracticeSurface } from "../../practice/PracticeSurface";
import { Button } from "../../ui/button";
import { SCALE_HUB_THEME as T } from "../../../lib/scales/scaleHubTheme";
import type { ScaleRecorridoHubCard } from "../../../lib/scales/scaleHubTypes";
import {
  buildScalePattern,
  buildScaleSequence,
} from "../../../lib/guitarEngine";
import { useGuitarSynth } from "../../../hooks/useGuitarSynth";
import { useInstrument } from "../../../contexts/InstrumentContext";

const COUNTDOWN_TICK_MS = 880;
const COUNTDOWN_GO_MS = 1_000;

type StudioPhase = "intro" | "countdown" | "play" | "complete";

export interface ScaleRecorridoStudioProps {
  card: ScaleRecorridoHubCard;
  wasCompletedBefore: boolean;
  onBack: () => void;
  onComplete: () => void;
}

export function ScaleRecorridoStudio({
  card,
  wasCompletedBefore,
  onBack,
  onComplete,
}: ScaleRecorridoStudioProps) {
  const { surface } = useInstrument();
  const { playFret } = useGuitarSynth();
  const { root, scaleType, maxFret, octaves, direction, bpm = 60 } = card.payload;

  const [phase, setPhase] = useState<StudioPhase>("intro");
  const [countdown, setCountdown] = useState<GymCountdownPhase | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [seqIndex, setSeqIndex] = useState(0);
  const [playbackDone, setPlaybackDone] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const scalePattern = useMemo(
    () => buildScalePattern(root, scaleType, maxFret),
    [root, scaleType, maxFret]
  );

  const sequence = useMemo(() => {
    const seq = buildScaleSequence(root, scaleType, { octaves, direction, pattern: "linear" });
    return seq.filter((p) => p.fret <= maxFret);
  }, [root, scaleType, octaves, direction, maxFret]);

  const staticHighlights = useMemo(
    () => scalePattern.positions.filter((p) => p.fret <= maxFret),
    [scalePattern, maxFret]
  );

  const activePos = sequence[seqIndex] ?? null;

  const highlightedPositions = useMemo(() => {
    if (!activePos || !isPlaying) return staticHighlights;
    const activeKey = `${activePos.string}-${activePos.fret}`;
    return staticHighlights.map((p) =>
      `${p.string}-${p.fret}` === activeKey ? { ...p, highlight: "active" as const } : p
    );
  }, [staticHighlights, activePos, isPlaying]);

  const stopPlayback = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setIsPlaying(false);
  }, []);

  const startPlayback = useCallback(() => {
    if (sequence.length === 0) return;
    stopPlayback();
    setPlaybackDone(false);
    setSeqIndex(0);
    const first = sequence[0];
    playFret(first.string, first.fret, 400);
    setIsPlaying(true);

    const beatMs = (60 / bpm) * 1000;
    let idx = 0;
    intervalRef.current = setInterval(() => {
      idx += 1;
      if (idx >= sequence.length) {
        stopPlayback();
        setPlaybackDone(true);
        setSeqIndex(0);
        return;
      }
      setSeqIndex(idx);
      const pos = sequence[idx];
      playFret(pos.string, pos.fret, beatMs * 0.85);
    }, beatMs);
  }, [sequence, bpm, playFret, stopPlayback]);

  useEffect(() => () => stopPlayback(), [stopPlayback]);

  const handleBeginCountdown = useCallback(() => {
    setCountdown(3);
  }, []);

  useEffect(() => {
    if (countdown === null) return;
    if (countdown === "go") {
      const id = window.setTimeout(() => {
        setPhase("play");
        setCountdown(null);
      }, COUNTDOWN_GO_MS);
      return () => window.clearTimeout(id);
    }
    const id = window.setTimeout(() => {
      if (typeof countdown === "number") {
        if (countdown <= 1) setCountdown("go");
        else setCountdown(countdown - 1);
      }
    }, COUNTDOWN_TICK_MS);
    return () => window.clearTimeout(id);
  }, [countdown]);

  const handleFinish = useCallback(() => {
    stopPlayback();
    setPhase("complete");
    window.setTimeout(() => {
      onComplete();
    }, 900);
  }, [onComplete, stopPlayback]);

  const dimNeck = phase === "intro" || countdown !== null;
  const promptText =
    sequence.length > 0
      ? isPlaying
        ? `Nota ${seqIndex + 1}/${sequence.length}`
        : playbackDone
          ? "Recorrido completado"
          : `${sequence.length} notas · pulsa Reproducir`
      : "Sin recorrido disponible";

  if (surface === "keyboard") {
    return (
      <ModuleStudioShell
        title={card.title}
        subtitle={card.subtitle}
        onBack={onBack}
        accent={T.accent}
        border={T.border}
        textMain={T.textMain}
        textMuted={T.textMuted}
        textDim={T.textDim}
      >
        <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
          <p className="text-sm max-w-md" style={{ color: T.textMuted }}>
            Esta misión está optimizada para guitarra. Cambia el instrumento de sesión o abre el
            explorador libre para piano.
          </p>
          <Button onClick={onBack} className="mt-4" variant="outline">
            Volver al hub
          </Button>
        </div>
      </ModuleStudioShell>
    );
  }

  return (
    <ModuleStudioShell
      title={card.title}
      subtitle={card.subtitle}
      onBack={onBack}
      accent={T.accent}
      border={T.border}
      textMain={T.textMain}
      textMuted={T.textMuted}
      textDim={T.textDim}
    >
      <div className="flex-1 min-h-[min(62vh,560px)] flex flex-col items-center justify-center p-2 sm:p-4 relative rounded-2xl overflow-hidden">
        <div
          className="relative w-full flex-1 max-w-[min(96vw,1100px)] mx-auto flex items-center justify-center transition-opacity duration-500 ease-out"
          style={{ opacity: dimNeck ? 0.35 : 1 }}
        >
          {staticHighlights.length > 0 ? (
            <PracticeSurface
              className="h-full w-full"
              highlightedPositions={highlightedPositions}
              showNoteNames
              showDegrees
              visualStyle="lutherie"
            />
          ) : (
            <p className="text-sm" style={{ color: T.textMuted }}>
              No hay notas de escala en este rango.
            </p>
          )}
        </div>

        {phase === "intro" && (
          <ModuleIntroModal
            title={card.title}
            subtitle={card.subtitle}
            description={card.description}
            instructions={card.instructions}
            tags={card.tags}
            timeEstimateMin={card.timeEstimateMin}
            accent={T.accent}
            accentSoft={T.accentSoft}
            accentGlow={T.accentGlow}
            borderHover={T.borderHover}
            textMain={T.textMain}
            textMuted={T.textMuted}
            textDim={T.textDim}
            wasCompletedBefore={wasCompletedBefore}
            onBegin={handleBeginCountdown}
          />
        )}

        {countdown !== null && <GymCountdownOverlay phase={countdown} />}

        {phase === "play" && (
          <>
            <div className="absolute top-3 left-1/2 z-30 w-[min(92%,420px)] -translate-x-1/2 pointer-events-none">
              <div
                className="flex items-center gap-3 px-4 py-3 rounded-2xl"
                style={{
                  background: "rgba(28,26,25,0.9)",
                  border: `1px solid ${T.borderHover}`,
                  boxShadow: "0 12px 40px rgba(0,0,0,0.45)",
                }}
              >
                <span
                  className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0"
                  style={{ backgroundColor: T.accentSoft, color: T.accent }}
                >
                  ♪
                </span>
                <p className="text-sm font-medium flex-1" style={{ color: T.textMain }}>
                  {promptText}
                  {activePos && isPlaying && (
                    <span className="block text-[11px] font-normal mt-0.5" style={{ color: T.textDim }}>
                      {activePos.note} · grado {activePos.degree}
                    </span>
                  )}
                </p>
              </div>
            </div>

            <div className="absolute bottom-4 left-1/2 z-30 -translate-x-1/2 flex flex-wrap items-center justify-center gap-2 pointer-events-auto px-2">
              <button
                type="button"
                onClick={() => (isPlaying ? stopPlayback() : startPlayback())}
                className="w-10 h-10 rounded-xl flex items-center justify-center hover:bg-white/10"
                style={{ border: `1px solid ${T.border}`, color: T.textMuted }}
                aria-label={isPlaying ? "Pausar" : "Reproducir"}
              >
                {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
              </button>
              <Button
                onClick={handleFinish}
                disabled={!playbackDone}
                className="font-semibold text-white border-0 px-5"
                style={{
                  backgroundColor: playbackDone ? T.accent : "rgba(255,255,255,0.12)",
                  opacity: playbackDone ? 1 : 0.7,
                }}
              >
                {playbackDone ? "Completar misión" : "Reproduce el recorrido"}
              </Button>
            </div>
          </>
        )}

        {phase === "complete" && (
          <div className="absolute inset-0 z-50 flex items-center justify-center pointer-events-none">
            <div
              className="rounded-2xl px-8 py-6 text-center module-complete-pop"
              style={{
                background: "rgba(28,26,25,0.94)",
                border: `1px solid ${T.accent}`,
                boxShadow: `0 0 40px ${T.accentGlow}`,
              }}
            >
              <p className="text-lg font-bold" style={{ color: T.textMain }}>
                ¡Misión completada!
              </p>
              <p className="text-xs mt-1" style={{ color: T.textDim }}>
                Volviendo al hub…
              </p>
            </div>
            <style>{`
              .module-complete-pop { animation: moduleCompletePop 0.5s ease-out both; }
              @keyframes moduleCompletePop {
                from { opacity: 0; transform: scale(0.92); }
                to { opacity: 1; transform: scale(1); }
              }
            `}</style>
          </div>
        )}
      </div>
    </ModuleStudioShell>
  );
}
